import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { CampaignLimit } from '@/ducks/campaigns/campaigns.types'

import Styles from './CampaignsTable.module.scss'

const LIMIT_OPTIONS: CampaignLimit[] = [10, 20, 50, 100]

type Props = {
  page: number
  limit: CampaignLimit
  total: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: CampaignLimit) => void
}

const getVisiblePages = (currentPage: number, totalPages: number): (number | 'ellipsis')[] => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index)
  }

  const pages: (number | 'ellipsis')[] = [0]

  if (currentPage > 2) {
    pages.push('ellipsis')
  }

  const start = Math.max(1, currentPage - 1)
  const end = Math.min(totalPages - 2, currentPage + 1)

  for (let page = start; page <= end; page += 1) {
    pages.push(page)
  }

  if (currentPage < totalPages - 3) {
    pages.push('ellipsis')
  }

  if (totalPages > 1) {
    pages.push(totalPages - 1)
  }

  return pages
}

const CampaignsTableFooter = ({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange
}: Props) => {
  const { t } = useTranslation()
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const visiblePages = useMemo(
    () => getVisiblePages(page, totalPages),
    [page, totalPages]
  )

  return (
    <div className={Styles.footer} data-testid="campaigns-table-footer">
      <div className={Styles.rowsPerPage}>
        <span>{t('marketing.campaigns.rows-per-page')}</span>
        <select
          className={Styles.rowsPerPageSelect}
          value={limit}
          aria-label={t('marketing.campaigns.rows-per-page')}
          onChange={event =>
            onLimitChange(Number(event.target.value) as CampaignLimit)
          }>
          {LIMIT_OPTIONS.map(option => (
            <option key={option} value={option}>
              {String(option).padStart(2, '0')}
            </option>
          ))}
        </select>
      </div>

      <div className={Styles.pagination}>
        <button
          type="button"
          className={Styles.pageNavButton}
          disabled={page <= 0}
          aria-label={t('marketing.campaigns.pagination.previous')}
          onClick={() => onPageChange(page - 1)}>
          <ChevronLeft width={16} height={16} />
        </button>

        {visiblePages.map((pageItem, index) =>
          pageItem === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className={Styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={pageItem}
              type="button"
              className={`${Styles.pageButton} ${
                pageItem === page ? Styles.active : ''
              }`}
              onClick={() => onPageChange(pageItem)}>
              {pageItem + 1}
            </button>
          )
        )}

        <button
          type="button"
          className={`${Styles.pageNavButton} ${Styles.nextButton}`}
          disabled={page >= totalPages - 1}
          aria-label={t('marketing.campaigns.pagination.next')}
          onClick={() => onPageChange(page + 1)}>
          <ChevronRight width={16} height={16} />
        </button>
      </div>
    </div>
  )
}

export default CampaignsTableFooter
